import React, { type FunctionComponent, isValidElement, useMemo } from 'react';

type IconSizeType = 'lg' | 'md' | 'sm' | 'xs';

type IconProps = { [key: string]: unknown };

function childIsIconComponent(child: React.ReactElement): boolean {
  return (child.type as FunctionComponent).name === 'Icon';
}

const convertIconSizeToIconButtonSize = (size: IconSizeType) => {
  switch (size) {
    case 'xs':
    case 'sm':
      return 'small';
    case 'md':
      return 'medium';
    case 'lg':
      return 'large';
    default:
      return 'medium';
  }
};

function isIconSizeType(value: unknown): value is IconSizeType {
  return value === 'lg' || value === 'md' || value === 'sm' || value === 'xs';
}

export const useInferButtonSize = (
  propName: string,
  children: React.ReactNode
) => {
  const element = useMemo(
    () =>
      React.Children.toArray(children).find(
        (child) =>
          isValidElement<IconProps>(child) &&
          childIsIconComponent(child) &&
          child.props[propName] !== undefined
      ),
    [children, propName]
  );

  const size = useMemo(() => {
    if (isValidElement<IconProps>(element)) {
      const propValue = element.props[propName];
      if (isIconSizeType(propValue)) {
        return convertIconSizeToIconButtonSize(propValue);
      }
    }
    return undefined;
  }, [element, propName]);

  return size;
};
